import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, HeadphonesIcon, Send, Sparkles, AlertTriangle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { vpCopilotSuggest, vpCopilotEvents } from "@/lib/api";

const field = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const btnPrimary = "inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 px-3 py-2 text-sm font-semibold transition";

export default function CopilotConsole() {
  const { user } = useAuth();
  const [msg, setMsg] = useState("");
  const [summary, setSummary] = useState("");
  const [suggestion, setSuggestion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const reload = useCallback(() => vpCopilotEvents(20).then((d) => setEvents(d.items || [])).catch(() => {}), []);
  useEffect(() => { if (user) reload(); }, [user, reload]);
  if (user === null) return <div className="min-h-screen flex flex-col"><Navbar /><div className="flex-1 grid place-items-center"><Link to="/login" className={btnPrimary}>Sign in</Link></div></div>;

  const ask = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      const r = await vpCopilotSuggest({ last_customer_message: msg, conversation_summary: summary, channel: "web" });
      setSuggestion(r);
      reload();
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="copilot-console">
        <div className="max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-indigo-700">
            <HeadphonesIcon className="w-3.5 h-3.5" /> Employee Copilot
          </div>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">Private suggestions to your human agents</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">Paste the latest customer utterance during a live human-managed conversation. Copilot suggests a draft reply, next-best action, policy reminders, sentiment and escalation. <b>Nothing is sent automatically.</b></p>

          <div className="mt-6 grid lg:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <label className="text-xs font-semibold text-slate-700 uppercase">Latest customer message</label>
              <textarea rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} className={field + " mt-1"} data-testid="cp-msg" />
              <label className="text-xs font-semibold text-slate-700 uppercase mt-3 block">Conversation summary so far (optional)</label>
              <textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} className={field + " mt-1"} data-testid="cp-summary" />
              <div className="mt-4 flex justify-end">
                <button onClick={ask} disabled={busy || !msg.trim()} className={btnPrimary} data-testid="cp-ask">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Get suggestion
                </button>
              </div>
              {suggestion && (
                <div className="mt-4 space-y-2 text-sm" data-testid="cp-result">
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                    <div className="text-[10px] font-bold uppercase text-indigo-700 mb-1">Draft reply</div>
                    <div>{suggestion.draft_reply || <span className="text-slate-500 italic">(none)</span>}</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Next-best action</div>
                    <div>{suggestion.next_best_action}</div>
                  </div>
                  {suggestion.escalate && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5" />
                      <div><b>Escalate:</b> {suggestion.escalate_reason || "flagged"}</div>
                    </div>
                  )}
                  {suggestion.policy_reminders?.length > 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <div className="text-[10px] font-bold uppercase text-amber-800 mb-1">Policy reminders</div>
                      <ul className="list-disc list-inside">
                        {suggestion.policy_reminders.map((p, i) => <li key={i}>{p}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="text-xs text-slate-500">Sentiment: {suggestion.sentiment}</div>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-bold text-slate-900 mb-3">Recent suggestions ({events.length})</div>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {events.length === 0 && <div className="text-xs text-slate-500">No suggestions yet.</div>}
                {events.map((e) => (
                  <div key={e.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid={`cp-event-${e.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">{e.at?.slice(0, 16)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${e.suggestions?.escalate ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{e.suggestions?.escalate ? "escalate" : e.suggestions?.sentiment || "n/a"}</span>
                    </div>
                    <div className="mt-1 text-slate-700 italic truncate">"{e.customer_message}"</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
